import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ModeleOrdonnancePanelService } from 'src/app/services/modele-ordonnance-panel/modele-ordonnance-panel.service';
import { ModeleOrdonnanceResume } from 'src/app/utils/interfaces/new/ModeleOrdonnanceResume';
import { EnregistrerModeleOrdonnanceAction } from 'src/app/utils/interfaces/new/EnregistrerModeleOrdonnanceAction';

interface ChoixModeleOrdonnance {
  type: 'nouveau' | 'existant';
  id?: string;
  nom: string;
}

@Component({
  selector: 'frg-enregistrer-modele-ordonnance',
  templateUrl: './enregistrer-modele-ordonnance.fragment.html',
  styleUrls: ['./enregistrer-modele-ordonnance.fragment.scss']
})
export class EnregistrerModeleOrdonnanceFragment {
  @Input() error = '';

  @Output() closed = new EventEmitter<void>();
  @Output() validate = new EventEmitter<EnregistrerModeleOrdonnanceAction>();
  @Output() clearError = new EventEmitter<void>();

  public nom = '';
  public rechercheEffectuee = false;
  public isLoading = false;
  public isLoadingMore = false;

  public nouveauChoix: ChoixModeleOrdonnance = null;
  public resultats: ChoixModeleOrdonnance[] = [];
  public choixSelectionne: ChoixModeleOrdonnance = null;

  public currentPage = 1;
  public readonly perPage = 30;
  public total = 0;
  private readonly nomModeleRegex = /^[A-Za-zÀ-ÿ0-9 _'’().-]+$/;

  constructor(
    private _modeleOrdonnancePanel: ModeleOrdonnancePanelService
  ) {}

  public onNomChange(event: Event): void {
    this.clearError.emit();

    const input = event.target as HTMLInputElement;

    this.nom = input.value;
    this.rechercheEffectuee = false;
    this.nouveauChoix = null;
    this.resultats = [];
    this.choixSelectionne = null;
    this.currentPage = 1;
    this.total = 0;
  }

  public get nomValide(): boolean {
    const nom = this.cleanName(this.nom);
    return !!nom && this.nomModeleRegex.test(nom);
  }

  public async rechercher(): Promise<void> {
    this.clearError.emit();
    const nom = this.cleanName(this.nom);
    if (!nom || !this.nomModeleRegex.test(nom) || this.isLoading) {
      return;
    }


    this.nom = nom;
    this.currentPage = 1;
    this.total = 0;
    this.nouveauChoix = null;
    this.resultats = [];
    this.choixSelectionne = null;
    this.rechercheEffectuee = false;
    this.isLoading = true;

    try {
      const result = await this._modeleOrdonnancePanel.find(
        this.currentPage,
        this.perPage,
        nom,
        true
      );

      const modeles: ModeleOrdonnanceResume[] = result.objects || [];
      this.total = Number(result.total || 0);

      const nomNormalise = this.normalizeName(nom);
      const modeleExact = modeles.find(
        modele => this.normalizeName(modele.nom) === nomNormalise
      );

      if (!modeleExact) {
        this.nouveauChoix = {
          type: 'nouveau',
          nom
        };
      }

      this.resultats = modeles.map(modele => ({
        type: 'existant',
        id: modele.id,
        nom: modele.nom
      }));

      this.rechercheEffectuee = true;
    } catch (error) {
      this.rechercheEffectuee = false;
      this.nouveauChoix = null;
      this.resultats = [];
      this.choixSelectionne = null;
    } finally {
      this.isLoading = false;
    }
  }

  public async loadMore(): Promise<void> {
    if (!this.hasMore || this.isLoadingMore) return;

    const nextPage = this.currentPage + 1;

    this.isLoadingMore = true;

    try {
      const result = await this._modeleOrdonnancePanel.find(
        nextPage,
        this.perPage,
        this.nom
      );

      if (!result) return;

      const nouveauxResultats = (result.objects || []).map(modele => ({
        type: 'existant' as const,
        id: modele.id,
        nom: modele.nom
      }));

      this.resultats.push(...nouveauxResultats);
      this.currentPage = nextPage;
      this.total = Number(result.total || this.total);
    } finally {
      this.isLoadingMore = false;
    }
  }

  public selectChoix(choix: ChoixModeleOrdonnance): void {
    if (!choix) return;
    this.choixSelectionne = choix;
  }

  public isSelected(choix: ChoixModeleOrdonnance): boolean {
    if (!choix || !this.choixSelectionne) return false;

    if (choix.type === 'nouveau') {
      return this.choixSelectionne.type === 'nouveau';
    }

    return this.choixSelectionne.type === 'existant'
      && this.choixSelectionne.id === choix.id;
  }

  public get hasMore(): boolean {
    return this.resultats.length < this.total;
  }

  public get actionLabel(): string {
    if (this.choixSelectionne?.type === 'existant') {
      return 'Remplacer';
    }

    return 'Créer';
  }

  public get actionDisabled(): boolean {
    return !this.choixSelectionne || this.isLoading || this.isLoadingMore;
  }

  public onCancel(): void {
    this.closed.emit();
  }

  public onValidate(): void {
    if (!this.choixSelectionne) return;

    if (this.choixSelectionne.type === 'nouveau') {
      this.validate.emit({
        action: 'create',
        nom: this.choixSelectionne.nom
      });
      return;
    }

    this.validate.emit({
      action: 'replace',
      nom: this.choixSelectionne.nom,
      idModele: this.choixSelectionne.id
    });
  }

  public trackByModele(index: number, choix: ChoixModeleOrdonnance): string {
    return choix.id || `nouveau-${choix.nom}`;
  }

  private cleanName(name: string): string {
    return (name || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private normalizeName(name: string): string {
    return this.cleanName(name)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }
}
